'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
	Camera,
	Upload,
	Zap,
	ZapOff,
	RefreshCw,
	Copy,
	Check,
	ExternalLink,
	QrCode,
	ShieldCheck,
	AlertCircle,
	SwitchCamera,
	ImageIcon,
	Loader2,
} from 'lucide-react';
import { BrowserMultiFormatReader } from '@zxing/browser';
import { BarcodeFormat } from '@zxing/library';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from '@/i18n/navigation';
import { cn } from '@/lib/utils';

// Standard native BarcodeDetector formats
const NATIVE_FORMATS = [
	'qr_code',
	'ean_13',
	'ean_8',
	'upc_a',
	'upc_e',
	'code_128',
	'code_39',
	'data_matrix',
];

interface NativeDetectedBarcode {
	rawValue: string;
	format: string;
}

interface NativeBarcodeDetectorInstance {
	detect(
		source: HTMLVideoElement | HTMLImageElement | HTMLCanvasElement,
	): Promise<NativeDetectedBarcode[]>;
}

interface WindowWithBarcodeDetector extends Window {
	BarcodeDetector?: {
		new (options?: { formats: string[] }): NativeBarcodeDetectorInstance;
	};
}

// Human-friendly format names mapping
const FORMAT_NAMES: Record<string, string> = {
	qr_code: 'QR Code',
	QR_CODE: 'QR Code',
	ean_13: 'EAN-13',
	EAN_13: 'EAN-13',
	ean_8: 'EAN-8',
	EAN_8: 'EAN-8',
	upc_a: 'UPC-A',
	UPC_A: 'UPC-A',
	upc_e: 'UPC-E',
	UPC_E: 'UPC-E',
	code_128: 'Code 128',
	CODE_128: 'Code 128',
	code_39: 'Code 39',
	CODE_39: 'Code 39',
	code_93: 'Code 93',
	CODE_93: 'Code 93',
	codabar: 'Codabar',
	CODABAR: 'Codabar',
	data_matrix: 'Data Matrix',
	DATA_MATRIX: 'Data Matrix',
	itf: 'ITF',
	ITF: 'ITF',
	pdf417: 'PDF-417',
	PDF_417: 'PDF-417',
	aztec: 'Aztec',
	AZTEC: 'Aztec',
	micro_qr_code: 'Micro QR',
	MICRO_QR_CODE: 'Micro QR',
};

function formatBarcodeType(format: string): string {
	if (FORMAT_NAMES[format]) return FORMAT_NAMES[format];
	return format.replace(/_/g, ' ').toUpperCase();
}

interface ScanResult {
	text: string;
	format: string;
}

export function BarcodeQrScanner() {
	const [activeTab, setActiveTab] = useState<'camera' | 'upload'>('camera');
	const [scannedResult, setScannedResult] = useState<ScanResult | null>(null);
	const [cameraError, setCameraError] = useState<string | null>(null);
	const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
	const [hasTorch, setHasTorch] = useState(false);
	const [isTorchOn, setIsTorchOn] = useState(false);
	const [copied, setCopied] = useState(false);
	const [isProcessingImage, setIsProcessingImage] = useState(false);
	const [uploadError, setUploadError] = useState<string | null>(null);
	const [dragOver, setDragOver] = useState(false);

	const videoRef = useRef<HTMLVideoElement | null>(null);
	const streamRef = useRef<MediaStream | null>(null);
	const animFrameRef = useRef<number | null>(null);
	const zxingReaderRef = useRef<BrowserMultiFormatReader | null>(null);
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const isScanningRef = useRef(false);

	// Stop camera stream & cancel detection loops
	const stopCamera = useCallback(() => {
		isScanningRef.current = false;
		if (animFrameRef.current) {
			cancelAnimationFrame(animFrameRef.current);
			animFrameRef.current = null;
		}

		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => {
				if (isTorchOn) {
					track
						.applyConstraints({ advanced: [{ torch: false } as MediaTrackConstraintSet] })
						.catch(() => {
							// Ignore torch constraint release errors
						});
				}
				track.stop();
			});
			streamRef.current = null;
		}

		if (videoRef.current) {
			videoRef.current.srcObject = null;
		}

		setIsTorchOn(false);
		setHasTorch(false);
	}, [isTorchOn]);

	// Successful scan handler
	const handleScanSuccess = useCallback(
		(text: string, format: string) => {
			if (!text) return;
			// Provide haptic feedback if supported
			if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
				try {
					navigator.vibrate(80);
				} catch {
					// Ignore vibration errors
				}
			}

			stopCamera();
			setScannedResult({ text, format });
		},
		[stopCamera],
	);

	// Start camera stream
	const startCamera = useCallback(async () => {
		setCameraError(null);

		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			setCameraError(
				'Camera access is not supported by your browser or connection (HTTPS required).',
			);
			return;
		}

		try {
			// Stop any existing stream first
			if (streamRef.current) {
				streamRef.current.getTracks().forEach((t) => t.stop());
				streamRef.current = null;
			}

			const constraints: MediaStreamConstraints = {
				video: {
					facingMode: { ideal: facingMode },
					width: { ideal: 1280 },
					height: { ideal: 720 },
				},
				audio: false,
			};

			const stream = await navigator.mediaDevices.getUserMedia(constraints);
			streamRef.current = stream;

			if (videoRef.current) {
				videoRef.current.srcObject = stream;
				await videoRef.current.play().catch(() => {
					// Autoplay might be temporarily interrupted
				});
			}

			isScanningRef.current = true;

			// Check torch capability
			const track = stream.getVideoTracks()[0];
			if (track && typeof track.getCapabilities === 'function') {
				const capabilities = track.getCapabilities() as MediaTrackCapabilities & {
					torch?: boolean;
				};
				setHasTorch(Boolean(capabilities?.torch));
			} else {
				setHasTorch(false);
			}

			// Initialize ZXing reader instance if needed
			if (!zxingReaderRef.current) {
				zxingReaderRef.current = new BrowserMultiFormatReader();
			}

			// Setup native BarcodeDetector if available
			let nativeDetector: NativeBarcodeDetectorInstance | null = null;
			const win = typeof window !== 'undefined' ? (window as WindowWithBarcodeDetector) : null;
			if (win && win.BarcodeDetector) {
				try {
					nativeDetector = new win.BarcodeDetector({
						formats: NATIVE_FORMATS,
					});
				} catch {
					nativeDetector = null;
				}
			}

			// Continuous detection loop
			let lastScanTime = 0;
			const scanLoop = (timestamp: number) => {
				if (!isScanningRef.current) return;

				void (async () => {
					const video = videoRef.current;
					if (
						video &&
						video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
						video.videoWidth > 0 &&
						video.videoHeight > 0
					) {
						// Run detection every 120ms to keep CPU / battery cool
						if (timestamp - lastScanTime > 120) {
							lastScanTime = timestamp;

							let foundText: string | null = null;
							let foundFormat: string | null = null;

							// 1. Try native BarcodeDetector API first
							if (nativeDetector) {
								try {
									const barcodes = await nativeDetector.detect(video);
									if (barcodes && barcodes.length > 0 && barcodes[0]?.rawValue) {
										foundText = barcodes[0].rawValue;
										foundFormat = formatBarcodeType(barcodes[0].format);
									}
								} catch {
									// Native detect failed, fallback to ZXing
								}
							}

							// 2. Fallback to @zxing/browser MultiFormatReader
							if (!foundText && zxingReaderRef.current) {
								try {
									const result = zxingReaderRef.current.decode(video);
									if (result) {
										foundText = result.getText();
										const rawFormat = BarcodeFormat[result.getBarcodeFormat()];
										foundFormat = formatBarcodeType(rawFormat || 'BARCODE');
									}
								} catch {
									// Frame doesn't contain a code yet - expected
								}
							}

							if (foundText && foundFormat) {
								handleScanSuccess(foundText, foundFormat);
								return;
							}
						}
					}

					if (isScanningRef.current) {
						animFrameRef.current = requestAnimationFrame(scanLoop);
					}
				})();
			};

			animFrameRef.current = requestAnimationFrame(scanLoop);
		} catch (err: unknown) {
			isScanningRef.current = false;
			const errorName = err instanceof Error ? err.name : '';
			const errorMessage =
				err instanceof Error ? err.message : 'Please verify device permissions and try again.';

			if (errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError') {
				setCameraError(
					'Camera permission was denied. Please allow camera access in your browser site settings or address bar, or upload an image file.',
				);
			} else if (errorName === 'NotFoundError' || errorName === 'DevicesNotFoundError') {
				setCameraError(
					'No camera device was found on this system. You can scan by uploading a picture or screenshot.',
				);
			} else {
				setCameraError('Unable to access the camera: ' + errorMessage);
			}
		}
	}, [facingMode, handleScanSuccess]);

	// Manage stream activation on tab switch & mount
	useEffect(() => {
		if (activeTab === 'camera' && !scannedResult) {
			void startCamera();
		} else {
			stopCamera();
		}

		return () => {
			stopCamera();
		};
	}, [activeTab, scannedResult, facingMode, startCamera, stopCamera]);

	// Toggle torch/flashlight
	const toggleTorch = () => {
		if (!streamRef.current) return;
		const track = streamRef.current.getVideoTracks()[0];
		if (!track) return;

		const nextTorch = !isTorchOn;
		track
			.applyConstraints({
				advanced: [{ torch: nextTorch } as MediaTrackConstraintSet],
			})
			.then(() => {
				setIsTorchOn(nextTorch);
			})
			.catch((err: unknown) => {
				console.error('Torch toggle failed:', err);
			});
	};

	// Toggle front / back camera
	const toggleCameraFacing = () => {
		stopCamera();
		setFacingMode((prev) => (prev === 'environment' ? 'user' : 'environment'));
	};

	// Decode barcode from image file
	const processImageFile = useCallback(
		async (file: File) => {
			if (!file || !file.type.startsWith('image/')) {
				setUploadError('Please select a valid image file (PNG, JPG, WebP, etc.).');
				return;
			}

			setIsProcessingImage(true);
			setUploadError(null);

			const objectUrl = URL.createObjectURL(file);
			const img = new Image();

			img.onload = () => {
				void (async () => {
					try {
						let foundText: string | null = null;
						let foundFormat: string | null = null;

						// 1. Try native BarcodeDetector
						const win =
							typeof window !== 'undefined' ? (window as WindowWithBarcodeDetector) : null;
						if (win && win.BarcodeDetector) {
							try {
								const detector = new win.BarcodeDetector({
									formats: NATIVE_FORMATS,
								});
								const barcodes = await detector.detect(img);
								if (barcodes && barcodes.length > 0 && barcodes[0]?.rawValue) {
									foundText = barcodes[0].rawValue;
									foundFormat = formatBarcodeType(barcodes[0].format);
								}
							} catch {
								// Fallback to ZXing
							}
						}

						// 2. Try ZXing reader
						if (!foundText) {
							try {
								if (!zxingReaderRef.current) {
									zxingReaderRef.current = new BrowserMultiFormatReader();
								}
								const result = await zxingReaderRef.current.decodeFromImageElement(img);
								if (result) {
									foundText = result.getText();
									const rawFormat = BarcodeFormat[result.getBarcodeFormat()];
									foundFormat = formatBarcodeType(rawFormat || 'BARCODE');
								}
							} catch {
								// Decode failed
							}
						}

						// 3. Fallback: downscale or canvas extraction if image is huge
						if (!foundText) {
							try {
								const canvas = document.createElement('canvas');
								const maxDim = 1600;
								let { width, height } = img;
								if (width > maxDim || height > maxDim) {
									if (width > height) {
										height = Math.round((height * maxDim) / width);
										width = maxDim;
									} else {
										width = Math.round((width * maxDim) / height);
										height = maxDim;
									}
								}
								canvas.width = width;
								canvas.height = height;
								const ctx = canvas.getContext('2d');
								if (ctx) {
									ctx.drawImage(img, 0, 0, width, height);
									if (!zxingReaderRef.current) {
										zxingReaderRef.current = new BrowserMultiFormatReader();
									}
									const result = zxingReaderRef.current.decodeFromCanvas(canvas);
									if (result) {
										foundText = result.getText();
										const rawFormat = BarcodeFormat[result.getBarcodeFormat()];
										foundFormat = formatBarcodeType(rawFormat || 'BARCODE');
									}
								}
							} catch {
								// Canvas decode failed
							}
						}

						if (foundText && foundFormat) {
							handleScanSuccess(foundText, foundFormat);
						} else {
							setUploadError(
								'No readable barcode or QR code detected. Try uploading a clearer, higher-contrast picture with the code centered.',
							);
						}
					} catch (err: unknown) {
						const msg = err instanceof Error ? err.message : 'Unknown error';
						setUploadError('Failed to read image: ' + msg);
					} finally {
						URL.revokeObjectURL(objectUrl);
						setIsProcessingImage(false);
					}
				})();
			};

			img.onerror = () => {
				URL.revokeObjectURL(objectUrl);
				setUploadError('Could not load the selected image file. Please try another file.');
				setIsProcessingImage(false);
			};

			img.src = objectUrl;
		},
		[handleScanSuccess],
	);

	// File drop handlers
	const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
		e.preventDefault();
		setDragOver(false);
		if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
			const file = e.dataTransfer.files[0];
			if (file) {
				void processImageFile(file);
			}
		}
	};

	const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files && e.target.files.length > 0) {
			const file = e.target.files[0];
			if (file) {
				void processImageFile(file);
			}
		}
	};

	// Paste listener (Ctrl+V / Cmd+V screenshot paste)
	useEffect(() => {
		const handlePaste = (e: ClipboardEvent) => {
			if (scannedResult) return;
			const items = e.clipboardData?.items;
			if (!items) return;
			for (let i = 0; i < items.length; i++) {
				const item = items[i];
				if (item && item.type.startsWith('image/')) {
					const file = item.getAsFile();
					if (file) {
						setActiveTab('upload');
						void processImageFile(file);
						break;
					}
				}
			}
		};

		window.addEventListener('paste', handlePaste);
		return () => window.removeEventListener('paste', handlePaste);
	}, [scannedResult, processImageFile]);

	// Copy to clipboard
	const handleCopy = () => {
		if (!scannedResult) return;
		navigator.clipboard
			.writeText(scannedResult.text)
			.then(() => {
				setCopied(true);
				setTimeout(() => setCopied(false), 2000);
			})
			.catch((err: unknown) => {
				console.error('Failed to copy text:', err);
			});
	};

	// Reset to scan again
	const handleScanAgain = () => {
		setScannedResult(null);
		setUploadError(null);
		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const isUrl = Boolean(
		scannedResult?.text &&
		(scannedResult.text.startsWith('http://') || scannedResult.text.startsWith('https://')),
	);

	return (
		<div className="w-full max-w-2xl mx-auto">
			{/* Mode Tabs */}
			<div className="flex justify-center mb-6">
				<div className="inline-flex p-1 bg-slate-100 dark:bg-slate-800/80 rounded-2xl border border-slate-200/80 dark:border-slate-700/80 shadow-xs">
					<button
						type="button"
						onClick={() => {
							setActiveTab('camera');
							if (scannedResult) setScannedResult(null);
						}}
						className={cn(
							'flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all cursor-pointer',
							activeTab === 'camera'
								? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-semibold'
								: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
						)}
					>
						<Camera className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
						Live Camera
					</button>

					<button
						type="button"
						onClick={() => {
							setActiveTab('upload');
							if (scannedResult) setScannedResult(null);
						}}
						className={cn(
							'flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium text-sm transition-all cursor-pointer',
							activeTab === 'upload'
								? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs font-semibold'
								: 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white',
						)}
					>
						<Upload className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
						Upload Image
					</button>
				</div>
			</div>

			{/* Main Container Card */}
			<div className="relative bg-white dark:bg-slate-900 rounded-3xl border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
				<AnimatePresence mode="wait">
					{scannedResult ? (
						/* Scanned Result Display */
						<motion.div
							key="result"
							initial={{ opacity: 0, scale: 0.96 }}
							animate={{ opacity: 1, scale: 1 }}
							exit={{ opacity: 0, scale: 0.96 }}
							transition={{ duration: 0.2 }}
							className="p-6 sm:p-8 space-y-6"
						>
							<div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 pb-4">
								<div className="flex items-center gap-2.5">
									<div className="w-9 h-9 rounded-xl bg-emerald-50 dark:bg-emerald-950/60 border border-emerald-200 dark:border-emerald-800 flex items-center justify-center text-emerald-600 dark:text-emerald-400">
										<Check className="h-5 w-5 stroke-[2.5]" />
									</div>
									<div>
										<h3 className="text-base font-semibold text-slate-900 dark:text-white">
											Code Detected Successfully
										</h3>
										<p className="text-xs text-slate-500 dark:text-slate-400">
											Decoded instantly in your browser
										</p>
									</div>
								</div>

								<Badge className="bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800 font-mono text-xs px-2.5 py-1">
									{scannedResult.format}
								</Badge>
							</div>

							{/* Scanned Value Box */}
							<div>
								<label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
									Scanned Value
								</label>
								<div className="p-4 bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 rounded-2xl font-mono text-sm text-slate-900 dark:text-slate-100 break-all select-all max-h-56 overflow-y-auto leading-relaxed">
									{scannedResult.text}
								</div>
							</div>

							{/* Primary Action Buttons */}
							<div className="flex flex-wrap items-center gap-3 pt-2">
								{isUrl && (
									<a
										href={scannedResult.text}
										target="_blank"
										rel="noopener noreferrer"
										className={cn(
											buttonVariants({ variant: 'default' }),
											'inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow-xs px-4 py-2.5',
										)}
									>
										<ExternalLink className="h-4 w-4" />
										Open URL
									</a>
								)}

								<Button
									type="button"
									variant="outline"
									onClick={handleCopy}
									className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
								>
									{copied ? (
										<>
											<Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
											<span className="font-semibold text-emerald-600 dark:text-emerald-400">
												Copied!
											</span>
										</>
									) : (
										<>
											<Copy className="h-4 w-4 text-slate-500" />
											<span>Copy Text</span>
										</>
									)}
								</Button>

								<Link
									href={`/create?text=${encodeURIComponent(scannedResult.text)}`}
									className={cn(
										buttonVariants({ variant: isUrl ? 'outline' : 'default' }),
										'inline-flex items-center gap-2 rounded-xl px-4 py-2.5',
										!isUrl && 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs',
									)}
								>
									<QrCode className="h-4 w-4" />
									Create QR Code with this Value
								</Link>

								<Button
									type="button"
									variant="ghost"
									onClick={handleScanAgain}
									className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white ml-auto"
								>
									<RefreshCw className="h-4 w-4" />
									Scan Again
								</Button>
							</div>
						</motion.div>
					) : activeTab === 'camera' ? (
						/* Live Camera Viewfinder */
						<motion.div
							key="camera"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="relative flex flex-col items-center justify-center min-h-[380px] sm:min-h-[440px] bg-slate-950 text-white"
						>
							{cameraError ? (
								<div className="p-8 text-center max-w-md space-y-4">
									<div className="w-12 h-12 rounded-2xl bg-rose-500/10 border border-rose-500/20 text-rose-400 flex items-center justify-center mx-auto">
										<AlertCircle className="h-6 w-6" />
									</div>
									<h4 className="text-base font-semibold text-white">Camera Access Needed</h4>
									<p className="text-sm text-slate-300 leading-relaxed">{cameraError}</p>
									<div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
										<Button
											type="button"
											onClick={() => void startCamera()}
											className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm"
										>
											<RefreshCw className="h-4 w-4 mr-2" />
											Try Again
										</Button>
										<Button
											type="button"
											variant="outline"
											onClick={() => setActiveTab('upload')}
											className="bg-slate-900 border-slate-700 text-white hover:bg-slate-800 rounded-xl text-sm"
										>
											<Upload className="h-4 w-4 mr-2" />
											Upload Image Instead
										</Button>
									</div>
								</div>
							) : (
								<>
									{/* Video Feed */}
									<video
										ref={videoRef}
										playsInline
										muted
										autoPlay
										className="w-full h-[380px] sm:h-[440px] object-cover"
									/>

									{/* Viewfinder Target Reticle & Laser Sweep */}
									<div className="absolute inset-0 pointer-events-none flex items-center justify-center">
										{/* Darkened Vignette Outside Target Area */}
										<div className="relative w-64 h-64 sm:w-72 sm:h-72">
											{/* Target Corner Brackets */}
											<div className="absolute -top-1 -left-1 w-9 h-9 border-t-4 border-l-4 border-emerald-400 rounded-tl-xl shadow-xs" />
											<div className="absolute -top-1 -right-1 w-9 h-9 border-t-4 border-r-4 border-emerald-400 rounded-tr-xl shadow-xs" />
											<div className="absolute -bottom-1 -left-1 w-9 h-9 border-b-4 border-l-4 border-emerald-400 rounded-bl-xl shadow-xs" />
											<div className="absolute -bottom-1 -right-1 w-9 h-9 border-b-4 border-r-4 border-emerald-400 rounded-br-xl shadow-xs" />

											{/* Animated Laser Sweep Line */}
											<motion.div
												className="absolute left-2 right-2 h-0.5 bg-gradient-to-r from-transparent via-emerald-400 to-transparent shadow-[0_0_14px_#34d399]"
												animate={{
													top: ['5%', '95%', '5%'],
													opacity: [0.7, 1, 0.7],
												}}
												transition={{
													duration: 2.4,
													repeat: Infinity,
													ease: 'easeInOut',
												}}
											/>
										</div>
									</div>

									{/* Bottom Camera Controls Bar */}
									<div className="absolute bottom-4 left-4 right-4 flex items-center justify-between pointer-events-auto">
										<div className="px-3 py-1.5 rounded-full bg-black/60 backdrop-blur-md text-xs font-medium text-slate-200 border border-white/10 flex items-center gap-2">
											<span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
											Point at barcode or QR code
										</div>

										<div className="flex items-center gap-2">
											{hasTorch && (
												<button
													type="button"
													onClick={toggleTorch}
													aria-label={isTorchOn ? 'Turn flashlight off' : 'Turn flashlight on'}
													className={cn(
														'p-2.5 rounded-full backdrop-blur-md transition-colors border',
														isTorchOn
															? 'bg-amber-500 text-black border-amber-400'
															: 'bg-black/60 text-white border-white/10 hover:bg-black/80',
													)}
												>
													{isTorchOn ? <ZapOff className="h-4 w-4" /> : <Zap className="h-4 w-4" />}
												</button>
											)}

											<button
												type="button"
												onClick={toggleCameraFacing}
												aria-label="Switch front and back camera"
												className="p-2.5 rounded-full bg-black/60 text-white border border-white/10 hover:bg-black/80 backdrop-blur-md transition-colors"
											>
												<SwitchCamera className="h-4 w-4" />
											</button>
										</div>
									</div>
								</>
							)}
						</motion.div>
					) : (
						/* Upload Image / Screenshot Mode */
						<motion.div
							key="upload"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="p-6 sm:p-10"
						>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/*"
								onChange={handleFileInputChange}
								className="hidden"
							/>

							<div
								onDragOver={(e) => {
									e.preventDefault();
									setDragOver(true);
								}}
								onDragLeave={() => setDragOver(false)}
								onDrop={handleDrop}
								onClick={() => fileInputRef.current?.click()}
								className={cn(
									'flex flex-col items-center justify-center p-8 sm:p-12 rounded-2xl border-2 border-dashed transition-all cursor-pointer text-center',
									dragOver
										? 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20'
										: 'border-slate-200 dark:border-slate-800 hover:border-emerald-500/60 hover:bg-slate-50/60 dark:hover:bg-slate-800/40',
								)}
							>
								{isProcessingImage ? (
									<div className="flex flex-col items-center space-y-3">
										<Loader2 className="h-10 w-10 text-emerald-600 animate-spin" />
										<p className="text-sm font-medium text-slate-700 dark:text-slate-300">
											Analyzing image & decoding codes...
										</p>
									</div>
								) : (
									<>
										<div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-950/50 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-4 border border-emerald-100 dark:border-emerald-800/50 shadow-xs">
											<ImageIcon className="h-7 w-7" />
										</div>

										<h4 className="text-base font-semibold text-slate-900 dark:text-white mb-1">
											Drop an image or screenshot here
										</h4>
										<p className="text-sm text-slate-500 dark:text-slate-400 max-w-sm mb-4">
											Or click to browse your files. You can also paste directly with{' '}
											<kbd className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md">
												Cmd+V
											</kbd>{' '}
											or{' '}
											<kbd className="px-1.5 py-0.5 text-xs bg-slate-100 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-md">
												Ctrl+V
											</kbd>
											.
										</p>

										<div className="inline-flex items-center gap-1.5 text-xs text-slate-400">
											<span>Supports PNG, JPG, WebP, SVG, GIF, AVIF</span>
										</div>
									</>
								)}
							</div>

							{uploadError && (
								<div className="mt-4 p-4 rounded-xl bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-900/60 text-rose-700 dark:text-rose-300 text-sm flex items-start gap-3">
									<AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
									<div className="leading-snug">{uploadError}</div>
								</div>
							)}
						</motion.div>
					)}
				</AnimatePresence>
			</div>

			{/* Privacy Guarantee Badge */}
			<div className="mt-6 flex items-center justify-center">
				<div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-100/80 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-800 text-xs font-medium text-slate-600 dark:text-slate-400">
					<ShieldCheck className="h-4 w-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
					<span>100% Client-side. No camera images or barcode data are ever sent to a server.</span>
				</div>
			</div>
		</div>
	);
}
